import { createClient } from '../engine/api/client';

// The browser half of a federated (Halo) sign-in. The API holds Halo's tokens; the SPA holds only
// our short-lived access token, in memory. The refresh token never reaches JavaScript: it is an
// HttpOnly, SameSite=Strict cookie scoped to /auth/session, and every call that uses it carries
// `x-nova-session: cookie` (which also makes a cross-site forgery impossible — see
// src/api/identityRoutes.ts). A page reload restores the session with one refresh call.

interface StartResponse {
  authorize_url: string;
  code_verifier: string;
  state: string;
}
interface Tokens {
  access_token: string;
  expires_in: number;
  session_expires_at: string;
  tenant_id?: string;
  user_id?: string;
}
interface AuthContextResponse {
  tenant_id?: string;
  tenant_name?: string;
  strategies: { kind: string }[];
}

/** How an email's organisation signs in: SSO through its Halo, or NovaPSA's own password login. */
export interface SignInRoute {
  sso: boolean;
  tenantId: string | null;
  organisation: string | null;
}

export interface FederatedIdentity {
  accessToken: string;
  expiresAt: number;
  userId: string;
  email: string | null;
  tenantId: string | null;
}

const REFRESH_SKEW_MS = 30_000;

const anon = createClient(async () => null);

function claimsOf(token: string): Record<string, unknown> {
  try {
    return JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function safeStorage(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Who a federated sign-in is for. The staff app and the customer portal each keep their own session
 * (their own refresh cookie, storage keys and callback page), so one browser can hold both.
 */
export interface FederatedSessionOptions {
  audience: 'staff' | 'portal';
  /** `x-nova-session` value: picks the refresh cookie the API sets (see src/api/identityRoutes.ts). */
  cookieMode: 'cookie' | 'portal-cookie';
  /** Where Halo sends the browser back (must be an allowed redirect origin). */
  callbackPath: string;
  activeKey: string;
  pendingKey: string;
}

export function createFederatedSession(opts: FederatedSessionOptions) {
  const COOKIE_MODE = { 'x-nova-session': opts.cookieMode };
  const PENDING_KEY = opts.pendingKey;
  const ACTIVE_KEY = opts.activeKey;
  let current: FederatedIdentity | null = null;
  let inflight: Promise<FederatedIdentity | null> | null = null;
  const listeners = new Set<(s: FederatedIdentity | null) => void>();

  function accept(t: Tokens): FederatedIdentity {
    const c = claimsOf(t.access_token);
    current = {
      accessToken: t.access_token,
      expiresAt: Date.now() + t.expires_in * 1000,
      userId: String(c['sub'] ?? t.user_id ?? ''),
      email: typeof c['email'] === 'string' ? c['email'] : null,
      tenantId: typeof c['tenant_id'] === 'string' ? c['tenant_id'] : t.tenant_id ?? null,
    };
    safeStorage('local')?.setItem(ACTIVE_KEY, '1');
    for (const l of listeners) l(current);
    return current;
  }

  function clear(): void {
    current = null;
    safeStorage('local')?.removeItem(ACTIVE_KEY);
    for (const l of listeners) l(null);
  }

  async function refresh(): Promise<FederatedIdentity | null> {
    inflight ??= anon
      .request<Tokens>('/auth/session/refresh', { method: 'POST', headers: COOKIE_MODE })
      .then(accept)
      .catch(() => {
        clear();
        return null;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  }

  return {
    get: (): FederatedIdentity | null => current,

    subscribe(fn: (s: FederatedIdentity | null) => void): () => void {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    /** Was this browser signed in through Halo (so a reload should try to restore)? */
    wasActive: (): boolean => safeStorage('local')?.getItem(ACTIVE_KEY) === '1',

    /** Restore after a reload: one cookie refresh. */
    restore: (): Promise<FederatedIdentity | null> => refresh(),

    /** The current access token, refreshed shortly before it expires. */
    async token(): Promise<string | null> {
      if (current && current.expiresAt - Date.now() > REFRESH_SKEW_MS) return current.accessToken;
      return (await refresh())?.accessToken ?? null;
    },

    /** Does this email domain's organisation sign in with Halo? Returns its tenant id, or null. */
    /**
     * Which sign-in an email gets, by its domain: an organisation that has verified the domain and
     * signs in with its Halo gets SSO; anything else (no such organisation, or lookup failure) gets
     * the password login.
     */
    async signInRouteFor(email: string): Promise<SignInRoute> {
      const domain = (email.includes('@') ? email.split('@').pop()! : email).trim().toLowerCase();
      if (!domain) return { sso: false, tenantId: null, organisation: null };
      try {
        const ctx = await anon.request<AuthContextResponse>(`/auth/context?domain=${encodeURIComponent(domain)}`);
        const sso = Boolean(ctx.tenant_id) && ctx.strategies.some((s) => s.kind === 'halo');
        return { sso, tenantId: ctx.tenant_id ?? null, organisation: ctx.tenant_name ?? null };
      } catch {
        return { sso: false, tenantId: null, organisation: null };
      }
    },

    /** The organisation this site belongs to (by its host) and whether it signs in with Halo. */
    routeForHost(): Promise<SignInRoute> {
      return this.signInRouteFor(window.location.hostname);
    },

    /** Go to the tenant's Halo sign-in page. */
    async begin(tenantId: string): Promise<void> {
      const redirectUri = `${window.location.origin}${opts.callbackPath}`;
      const start = await anon.request<StartResponse>(
        `/auth/halo/start?tenant=${encodeURIComponent(tenantId)}&redirect_uri=${encodeURIComponent(redirectUri)}${opts.audience === 'portal' ? '&audience=portal' : ''}`,
      );
      safeStorage('session')?.setItem(PENDING_KEY, JSON.stringify({ tenant: tenantId, verifier: start.code_verifier, state: start.state, redirectUri }));
      window.location.assign(start.authorize_url);
    },

    /** Back from Halo with ?code&state: finish the sign-in server-side. */
    async complete(code: string, state: string): Promise<FederatedIdentity> {
      const raw = safeStorage('session')?.getItem(PENDING_KEY);
      safeStorage('session')?.removeItem(PENDING_KEY);
      if (!raw) throw new Error('This sign-in link has expired — start again.');
      const pending = JSON.parse(raw) as { tenant: string; verifier: string; state: string; redirectUri: string };
      if (pending.state !== state) throw new Error('This sign-in response does not match the request — start again.');
      const tokens = await anon.request<Tokens>('/auth/halo/callback', {
        method: 'POST',
        headers: COOKIE_MODE,
        body: { tenant: pending.tenant, code, code_verifier: pending.verifier, redirect_uri: pending.redirectUri, state },
      });
      return accept(tokens);
    },

    /** Sign out: revokes the server session (destroying the Halo tokens) and clears the cookie. */
    async signOut(): Promise<void> {
      try {
        await anon.request('/auth/session/logout', { method: 'POST', headers: COOKIE_MODE });
      } finally {
        clear();
      }
    },

    /** Use a new access token for the same session (after a second-factor step-up). */
    adopt: (t: { access_token: string; expires_in: number }): FederatedIdentity => accept(t as Tokens),

    /** Drop local state after the API rejected the token (the server session is gone). */
    forget: clear,
  };
}

/** The staff app's Halo sign-in. */
export const federatedSession = createFederatedSession({ audience: 'staff', cookieMode: 'cookie', callbackPath: '/login/halo/callback', activeKey: 'nova.fed', pendingKey: 'nova.halo.pending' });

/** The customer portal's Halo sign-in (a contact of a tenant running on its HaloPSA). */
export const portalFederatedSession = createFederatedSession({ audience: 'portal', cookieMode: 'portal-cookie', callbackPath: '/portal/auth/halo', activeKey: 'nova.portal.fed', pendingKey: 'nova.portal.halo.pending' });
